/**
 * 客户详情 ·「上传录音」Tab —— 销售替客户把录音传到这位客户的顾问书上。
 *
 * 为什么有这个入口：客户常把录音文件直接微信发给销售；小程序里只能「选微信聊天里的文件」，对普通用户有点绕。
 * 销售在这里替客户传，效果与客户自己在小程序里「上传已有录音」完全一样（同一条后端状态机：
 * 转码 → 识别 → [采访录音：选讲述人] → 入管线），客户在小程序里也看得到、能继续操作。
 *
 * 两种录音：
 *   · 采访录音（默认）：有人提问 + 讲述人回答（子女采访父母）。识别说话人后，**必须选定哪位是讲述人**，
 *     只有讲述人的话进书，提问者的话不进。永不自动入管线。
 *   · 只有讲述人：语音备忘、讲述人自己录的。转写后直接入管线。
 *   不确定就选「采访录音」——多一步选人，但绝不会把提问者的话当成讲述人的素材。默认也因此选「采访录音」。
 *
 * 读留痕：列表（轮询）不含任何讲述内容；说话人样例句（客户讲述原话）只在「选讲述人」弹窗里读详情接口时出现，
 * 该读取会被记一条 view_content。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Empty, Modal, Popconfirm, Progress, Radio, Select, Space, Spin, Table, Tag, Typography, Upload, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../contracts/permissions';
import {
  chooseAudioImportNarrator,
  completeAudioImport,
  createAudioImport,
  deleteAudioImport,
  getAudioImport,
  listAudioImports,
  retryAudioImport,
  uploadAudioImportChunk,
  type AdvisorCustomerDetailDto,
  type AudioImportDto,
  type AudioImportMode,
} from '../../../api/advisor';
import { narratorText } from './narrator';
import {
  ALLOWED_EXTENSIONS,
  MODE_LABEL,
  POLL_INTERVAL_MS,
  UploadCancelledError,
  describeImport,
  formatDuration,
  needsPolling,
  speakerSummary,
  uploadChunks,
  validateFile,
} from './audio-import';

const { Text, Paragraph } = Typography;

const errText = (e: any, fallback: string) => e?.response?.data?.message ?? fallback;
const formatTs = (ms: number) => new Date(ms).toLocaleString('zh-CN', { hour12: false });

type LocalUpload = { phase: 'creating' | 'uploading' | 'finishing'; pct: number; fileName: string };

export function AudioImportTab({ detail }: { detail: AdvisorCustomerDetailDto }) {
  const customerId = detail.customerId;
  const queryClient = useQueryClient();
  const canFulfill = usePermission(PERMISSIONS.ADVISOR_FULFILL);

  // 只有已付款开了书的订单才有书可传
  const books = useMemo(() => detail.orders.filter((o) => o.view === 'paid' && o.workId), [detail.orders]);
  const [workId, setWorkId] = useState<string | undefined>(books[0]?.workId ?? undefined);
  useEffect(() => {
    if (!workId || !books.some((b) => b.workId === workId)) setWorkId(books[0]?.workId ?? undefined);
  }, [books, workId]);
  const currentBook = useMemo(() => books.find((b) => b.workId === workId), [books, workId]);

  const [mode, setMode] = useState<AudioImportMode>('interview');
  const [local, setLocal] = useState<LocalUpload | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const uploading = local !== null;

  const listKey = ['advisor-audio-imports', customerId, workId];
  const listQuery = useQuery({
    queryKey: listKey,
    queryFn: () => listAudioImports(customerId, workId!),
    enabled: !!workId,
    refetchInterval: (query) => (needsPolling(query.state.data) ? POLL_INTERVAL_MS : false),
  });
  const imports = listQuery.data ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['advisor-audio-imports', customerId] });

  // 上传中离开页面会中断上传
  useEffect(() => {
    if (!uploading) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [uploading]);
  useEffect(() => () => abortRef.current?.abort(), []);

  const startUpload = async (file: File) => {
    if (!workId || uploading) return;
    const check = validateFile(file);
    if (!check.ok) {
      message.warning(check.message);
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLocal({ phase: 'creating', pct: 0, fileName: file.name });
    let createdId: string | null = null;
    let completeCalled = false;
    try {
      const created = await createAudioImport(customerId, workId, { fileName: file.name, sizeBytes: file.size, mode });
      createdId = created.importId;
      setLocal({ phase: 'uploading', pct: 0, fileName: file.name });
      await uploadChunks({
        file,
        chunkSize: created.chunkSize,
        totalChunks: created.totalChunks,
        send: (index, blob, signal) => uploadAudioImportChunk(customerId, workId, created.importId, index, blob, signal),
        onProgress: (pct) => setLocal((l) => (l ? { ...l, pct } : l)),
        signal: controller.signal,
      });
      setLocal((l) => (l ? { ...l, phase: 'finishing', pct: 100 } : l));
      completeCalled = true;
      await completeAudioImport(customerId, workId, created.importId);
      message.success('上传好了，正在识别');
    } catch (e) {
      if (e instanceof UploadCancelledError) {
        message.info('已取消上传');
      } else {
        message.error(errText(e, '上传没有成功，请重试'));
      }
      // 还没走到「完成」就失败 / 取消：清掉服务端留下的半成品。已经点过完成的不清——可能服务端其实已经收到了，
      // 让销售在列表里看真实状态再决定。
      if (createdId && !completeCalled) {
        await deleteAudioImport(customerId, workId, createdId).catch(() => undefined);
      }
    } finally {
      abortRef.current = null;
      setLocal(null);
      refresh();
    }
  };

  const retryMutation = useMutation({
    mutationFn: (v: AudioImportDto) => retryAudioImport(customerId, workId!, v.importId),
    onSuccess: () => {
      message.success('已重新开始处理');
      refresh();
    },
    onError: (e) => {
      message.error(errText(e, '重试失败，请稍后再试'));
      refresh();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (v: AudioImportDto) => deleteAudioImport(customerId, workId!, v.importId),
    onSuccess: () => {
      message.success('已删除');
      refresh();
    },
    onError: (e) => {
      message.error(errText(e, '删除失败，请稍后再试'));
    },
  });

  // ── 选讲述人 ──
  const [pickingId, setPickingId] = useState<string | null>(null);
  const [pickedSpeaker, setPickedSpeaker] = useState<string | null>(null);
  const pickQuery = useQuery({
    queryKey: ['advisor-audio-import', customerId, workId, pickingId],
    queryFn: () => getAudioImport(customerId, workId!, pickingId!),
    enabled: !!pickingId && !!workId,
    retry: false,
    staleTime: 0,
    gcTime: 0, // 样例句是客户讲述原话，不在前端缓存里留着
  });
  const closePicker = () => {
    setPickingId(null);
    setPickedSpeaker(null);
  };
  const narratorMutation = useMutation({
    mutationFn: (speakerId: string) => chooseAudioImportNarrator(customerId, workId!, pickingId!, speakerId),
    onSuccess: () => {
      message.success('已选定讲述人，正在整理成素材');
      closePicker();
      refresh();
    },
    onError: (e) => {
      message.error(errText(e, '没能选定讲述人，请稍后再试'));
      refresh();
    },
  });

  if (books.length === 0) {
    return <Empty description="客户付款后系统会自动开书，开书后才能在这里上传录音" />;
  }

  const bookSelect =
    books.length > 1 ? (
      <Space>
        <Text>选择书：</Text>
        <Select
          style={{ minWidth: 320 }}
          value={workId}
          onChange={setWorkId}
          disabled={uploading}
          options={books.map((o) => ({
            value: o.workId!,
            label: `${narratorText(o.narratorName, o.narratorRelation) || '未填讲述人'} · ${o.productTitle}`,
          }))}
        />
      </Space>
    ) : null;

  const columns: ColumnsType<AudioImportDto> = [
    {
      title: '文件',
      dataIndex: 'fileName',
      render: (name: string, v) => (
        <Space direction="vertical" size={0}>
          <Text ellipsis style={{ maxWidth: 260 }} title={name}>{name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {v.uploadedBy === 'sales' ? '销售代传' : '客户自己上传'} · {formatTs(v.createdAt)}
          </Text>
        </Space>
      ),
    },
    { title: '类型', dataIndex: 'mode', width: 100, render: (m: AudioImportDto['mode']) => MODE_LABEL[m] },
    { title: '时长', dataIndex: 'durationMs', width: 100, render: (ms: number | null) => formatDuration(ms) },
    {
      title: '状态',
      width: 320,
      render: (_, v) => {
        const badge = describeImport(v);
        return (
          <Space direction="vertical" size={2}>
            <Tag color={badge.color}>{badge.label}</Tag>
            <Text type={v.status === 'failed' ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>{badge.hint}</Text>
          </Space>
        );
      },
    },
    {
      title: '操作',
      width: 220,
      render: (_, v) =>
        canFulfill ? (
          <Space>
            {v.status === 'awaiting_speaker' && (
              <Button type="primary" size="small" onClick={() => { setPickedSpeaker(null); setPickingId(v.importId); }}>
                选讲述人
              </Button>
            )}
            {v.status === 'failed' && v.retryable && (
              <Button size="small" loading={retryMutation.isPending && retryMutation.variables?.importId === v.importId} onClick={() => retryMutation.mutate(v)}>
                重试
              </Button>
            )}
            <Popconfirm
              title="删除这条录音？"
              description="已经整理进书的内容不会撤回；只是把这条记录从列表里去掉。"
              okText="删除"
              cancelText="先留着"
              onConfirm={() => deleteMutation.mutate(v)}
            >
              <Button size="small" danger>删除</Button>
            </Popconfirm>
          </Space>
        ) : null,
    },
  ];

  const speakers = pickQuery.data?.status === 'awaiting_speaker' ? pickQuery.data.speakers ?? [] : [];
  const pickerStale = !!pickQuery.data && pickQuery.data.status !== 'awaiting_speaker';

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {bookSelect}

      <Alert
        type="info"
        showIcon
        message="替客户上传录音"
        description={
          <Paragraph style={{ marginBottom: 0 }}>
            客户把录音文件（如电话采访、手机语音备忘）发给你后，可以在这里替他传到
            {currentBook ? `「${narratorText(currentBook.narratorName, currentBook.narratorRelation) || '这位讲述人'}」的书` : '这本书'}
            里，效果和客户自己在小程序里上传一样，客户在小程序里也能看到。支持 {ALLOWED_EXTENSIONS.join('、')}，最大 200MB、最长 60 分钟。
          </Paragraph>
        }
      />

      {canFulfill && (
        <Card size="small" title="上传">
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Space direction="vertical" size={4}>
              <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)} disabled={uploading}>
                <Radio.Button value="interview">采访录音（有人提问）</Radio.Button>
                <Radio.Button value="solo">只有讲述人</Radio.Button>
              </Radio.Group>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {mode === 'interview'
                  ? '识别出各位说话人后，由你（或客户）选出哪位是讲述人，只有讲述人的话会进书，提问者的话不会。'
                  : '整段录音都当作讲述人本人说的话，直接整理成素材。录音里如果还有别人说话，请选「采访录音」。'}
              </Text>
            </Space>

            {local ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text>
                  {local.phase === 'creating' ? '正在准备…' : local.phase === 'finishing' ? '正在完成…' : '正在上传'}：{local.fileName}
                </Text>
                <Progress percent={local.pct} status={local.phase === 'finishing' ? 'active' : 'normal'} />
                {local.phase !== 'finishing' && (
                  <Button size="small" onClick={() => abortRef.current?.abort()}>取消上传</Button>
                )}
              </Space>
            ) : (
              <Upload
                accept={ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(',')}
                showUploadList={false}
                maxCount={1}
                beforeUpload={(file) => {
                  void startUpload(file as unknown as File);
                  return false;
                }}
              >
                <Button type="primary" disabled={!workId}>选择录音文件</Button>
              </Upload>
            )}
          </Space>
        </Card>
      )}

      <Card
        size="small"
        title="这本书的录音"
        extra={<Button size="small" onClick={() => listQuery.refetch()} loading={listQuery.isFetching}>刷新</Button>}
      >
        {listQuery.isError ? (
          <Alert
            type="error"
            showIcon
            message={(listQuery.error as any)?.response?.status === 404 ? '这本书不存在，或不属于该客户' : errText(listQuery.error, '加载失败')}
            action={<Button onClick={() => listQuery.refetch()}>重试</Button>}
          />
        ) : (
          <Table<AudioImportDto>
            rowKey="importId"
            size="small"
            loading={listQuery.isLoading}
            dataSource={imports}
            columns={columns}
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: '还没有录音。客户发来录音后，在上面选择文件上传' }}
          />
        )}
      </Card>

      <Modal
        title="哪一位是讲述人？"
        open={!!pickingId}
        onCancel={closePicker}
        okText="确认选择"
        cancelText="先不选"
        okButtonProps={{ disabled: !pickedSpeaker || pickerStale, loading: narratorMutation.isPending }}
        onOk={() => { if (pickedSpeaker) narratorMutation.mutate(pickedSpeaker); }}
        destroyOnHidden
        width={640}
      >
        {pickQuery.isLoading && <Spin />}
        {pickQuery.isError && (
          <Alert type="error" showIcon message={errText(pickQuery.error, '加载失败，请关闭后重试')} />
        )}
        {pickerStale && (
          <Alert
            type="warning"
            showIcon
            message="这条录音已经不在等待选讲述人了"
            description="可能客户已经在小程序里选过。关闭后刷新列表看最新状态。"
          />
        )}
        {speakers.length > 0 && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {pickQuery.data?.attribution === 'llm' && (
              <Alert type="warning" showIcon message="这次识别可能不准，请对照样例句核实" />
            )}
            <Alert
              type="info"
              showIcon
              message="只有被选中的人说的话会整理进书，另一方的话不会。选定后无法更改。"
              description="下面的样例句是客户讲述的原话，查看会被记录。"
            />
            <Radio.Group value={pickedSpeaker} onChange={(e) => setPickedSpeaker(e.target.value)} style={{ width: '100%' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {speakers.map((s) => (
                  <Card key={s.speakerId} size="small" hoverable onClick={() => setPickedSpeaker(s.speakerId)}>
                    <Radio value={s.speakerId}>
                      <Text strong>{speakerSummary(s)}</Text>
                    </Radio>
                    <div style={{ marginTop: 8, paddingLeft: 24 }}>
                      {s.sampleLines.length === 0 ? (
                        <Text type="secondary">（没有可展示的样例）</Text>
                      ) : (
                        s.sampleLines.map((line, i) => (
                          <div key={i}>
                            <Text type="secondary">“{line}”</Text>
                          </div>
                        ))
                      )}
                    </div>
                  </Card>
                ))}
              </Space>
            </Radio.Group>
          </Space>
        )}
        {pickQuery.data && pickQuery.data.status === 'awaiting_speaker' && speakers.length === 0 && (
          <Alert type="error" showIcon message="没有拿到说话人信息，请关闭后刷新再试" />
        )}
      </Modal>

      {local === null && imports.some((v) => v.status === 'transcribing' || v.status === 'ingesting') && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          有录音正在处理，页面会自动刷新（约每 {POLL_INTERVAL_MS / 1000} 秒）。
        </Text>
      )}
    </Space>
  );
}
